import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'manageSpaceModalTitle'
	| 'manageSpaceNameLabel'
	| 'manageSpaceNameHelperMessage'
	| 'manageSpaceAboutLabel'
	| 'manageSpaceSourcesLabel'
	| 'manageSpaceAddSources'
	| 'manageSpaceRemoveSource'
	| 'manageSpaceNameRequired'
	| 'manageSpaceEmojiPickerLabel'
	| 'manageSpaceCancel'
	| 'manageSpaceSubmit'
	| 'manageSpaceRequiredFieldsHint'
	| 'manageSpaceLoadError'
	| 'manageSpaceSaveError'
	| 'manageSpaceNoSourcesTitle'
	| 'manageSpaceNoSources';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		manageSpaceModalTitle: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.title',
			defaultMessage: 'Manage Rovo space',
			description: 'Title of the modal for managing (editing) an existing Rovo Space.',
		},
		manageSpaceNameLabel: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.name-label',
			defaultMessage: 'Name',
			description: 'Label for the required Rovo space name field in the manage Rovo space modal.',
		},
		manageSpaceNameHelperMessage: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.name-helper',
			defaultMessage: 'Give your Rovo space a short, clear name that explains what it does',
			description: 'Helper text under the Rovo space name field in the manage Rovo space modal.',
		},
		manageSpaceAboutLabel: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.about-label',
			defaultMessage: 'About',
			description:
				'Label for the optional Rovo space description field in the manage Rovo space modal.',
		},
		manageSpaceSourcesLabel: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.sources-label',
			defaultMessage: 'Sources',
			description: 'Label for the Rovo space sources field in the manage Rovo space modal.',
		},
		manageSpaceAddSources: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.add-sources',
			defaultMessage: 'Add sources',
			description: 'Button to open the flow for adding more sources to the Rovo space.',
		},
		manageSpaceRemoveSource: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.remove-source',
			defaultMessage: 'Remove {name}',
			description:
				'Accessibility label for the delete control on a source row; {name} is the source title.',
		},
		manageSpaceNameRequired: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.name-required',
			defaultMessage: 'Enter a name',
			description: 'Validation error when Rovo space name is empty in the manage Rovo space modal.',
		},
		manageSpaceEmojiPickerLabel: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.emoji-picker',
			defaultMessage: 'Choose emoji',
			description:
				'Accessibility label for opening the emoji picker next to the Rovo space name field in the manage Rovo space modal.',
		},
		manageSpaceCancel: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.cancel',
			defaultMessage: 'Cancel',
			description: 'Button to close the manage space modal without saving.',
		},
		manageSpaceSubmit: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.submit',
			defaultMessage: 'Save',
			description: 'Primary button to submit the manage space form.',
		},
		manageSpaceRequiredFieldsHint: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.required-fields-hint',
			defaultMessage: 'Required fields are marked with an asterisk <asterisk></asterisk>',
			description:
				'Informational text in the manage space form header: required fields use an asterisk.',
		},
		manageSpaceLoadError: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.load-error',
			defaultMessage: 'Failed to load space details. Please try again.',
			description: 'Error message shown when space details fail to load in the manage space modal.',
		},
		manageSpaceSaveError: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.save-error',
			defaultMessage: 'Failed to save changes. Please try again.',
			description: 'Error message shown when saving space changes fails.',
		},
		manageSpaceNoSourcesTitle: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.no-sources-title',
			defaultMessage: 'No sources added',
			description: 'Title shown in the empty sources state in the manage space modal.',
		},
		manageSpaceNoSources: {
			id: 'ai-mate.rovo-spaces.manage-space-modal.no-sources',
			defaultMessage: 'Add sources to give Rovo context for this Rovo space.',
			description: 'Body text shown in the empty sources state in the manage space modal.',
		},
	});

export default message;
